import {
  EventContentArg,
  EventSourceFuncArg,
  EventInput,
} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import { useCallback, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";

import { formatPrice } from "utils/formats";
import { fetchRepasseTotals } from "utils/repasse";
import { toast } from "utils/toast";

import * as S from "../../styles";

interface RepasseCalendarProps {
  companyId: number;
  professionalId: number;
  onMonthChange?: (agendamento: number, repassado: number) => void;
}

const RepasseCalendar = ({
  companyId,
  professionalId,
  onMonthChange,
}: RepasseCalendarProps) => {
  const [isLoading, setIsLoading] = useState(false);

  // Identidade estável: se a função `events` muda a cada render, o
  // FullCalendar a trata como nova fonte e refaz o fetch em loop.
  const fetchEvents = useCallback(
    async (
      info: EventSourceFuncArg,
      successCallback: (events: EventInput[]) => void,
      failureCallback: (error: Error) => void
    ) => {
      try {
        // Pagamentos (link ou máquina física) entram no repasse
        // 2 dias úteis depois (regra D+2: pagamento + split).
        const byDay = await fetchRepasseTotals({
          startDate: info.startStr.substring(0, 10),
          endDate: info.endStr.substring(0, 10),
          companyId,
          professionalId,
        });

        let totalAgendamento = 0;
        let totalRepassado = 0;

        const events = Object.entries(byDay).map(([date, totals]) => {
          totalAgendamento += totals.agendamento;
          totalRepassado += totals.repassado;
          return {
            start: date,
            extendedProps: totals,
          };
        });

        onMonthChange?.(totalAgendamento, totalRepassado);
        successCallback(events);
      } catch (error) {
        toast.fire({
          icon: "error",
          title: "Não foi possível carregar os repasses. Tente novamente.",
        });
        failureCallback(error as Error);
      }
    },
    [companyId, professionalId, onMonthChange]
  );

  function renderEventContent(eventInfo: EventContentArg) {
    // agendamento = repasses previstos para o dia (pagamentos D+2 úteis)
    // repassado   = orçamentos concluídos com repasse neste dia
    const agendamento: number = eventInfo.event._def.extendedProps.agendamento ?? 0;
    const repassado: number = eventInfo.event._def.extendedProps.repassado ?? 0;

    if (!agendamento && !repassado) return null;

    return (
      <Link
        to="/agenda/repasse-detalhes"
        state={{
          date: eventInfo.event.startStr,
          companyId,
          professionalId,
        }}
        style={{ textDecoration: "none", display: "block", width: "100%" }}
      >
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {agendamento > 0 && (
                <div>
                  Repasse previsto (D+2 úteis): {formatPrice(agendamento)}
                </div>
              )}
              {repassado > 0 && (
                <div>Já repassado: {formatPrice(repassado)}</div>
              )}
              <div>Clique para ver detalhes e gerar o CNAB 240</div>
            </Tooltip>
          }
        >
          <S.EventWrapper
            role="button"
            aria-label="Ver repasses do dia e gerar CNAB 240"
          >
            {agendamento > 0 && (
              <S.EventPill $type="repasse">
                <i className="uil uil-calendar-check" aria-hidden="true" />
                {formatPrice(agendamento)}
              </S.EventPill>
            )}
            {repassado > 0 && (
              <S.EventPill $type="paid">
                <i className="uil uil-check-circle" aria-hidden="true" />
                {formatPrice(repassado)}
              </S.EventPill>
            )}
          </S.EventWrapper>
        </OverlayTrigger>
      </Link>
    );
  }

  return (
    <S.CalendarShell>
    {isLoading && (
      <S.CalendarLoading aria-label="Carregando repasses">
        <div className="spinner-border text-primary" role="status" />
      </S.CalendarLoading>
    )}
    <FullCalendar
      plugins={[dayGridPlugin]}
      locale="pt-br"
      timeZone="UTC"
      initialView="dayGridMonth"
      headerToolbar={{
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,dayGridWeek",
      }}
      buttonText={{ today: "Hoje", month: "Mês", week: "Semana" }}
      buttonHints={{
        prev: "Anterior",
        next: "Próximo",
        today: "Hoje",
        month: "Mês",
        week: "Semana",
      }}
      height="auto"
      loading={setIsLoading}
      events={fetchEvents}
      eventContent={renderEventContent}
      eventColor="transparent"
    />
    </S.CalendarShell>
  );
};

export default RepasseCalendar;
